// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

/**
 * Web Audio plumbing: acquire the microphone and tap it with an AnalyserNode so
 * the tuner can read raw time-domain frames, and decode uploaded files. The
 * pitch maths lives in pitch.ts — this module only moves samples. The mic is
 * NEVER connected to the destination (no monitoring), which also sidesteps the
 * iOS earpiece-routing / feedback problem for a live-mic tab.
 */

const FFT_SIZE = 4096;

export function audioSupported(): boolean {
  return (
    typeof AudioContext !== 'undefined' ||
    typeof (window as unknown as { webkitAudioContext?: unknown }).webkitAudioContext !== 'undefined'
  );
}

function makeContext(): AudioContext {
  const Ctor =
    (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
      .AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) throw new Error('Web Audio is not supported in this browser.');
  return new Ctor();
}

export class MicrophoneError extends Error {
  readonly reason: 'denied' | 'missing' | 'insecure' | 'unknown';
  constructor(reason: MicrophoneError['reason'], message: string) {
    super(message);
    this.name = 'MicrophoneError';
    this.reason = reason;
  }
}

/**
 * A live microphone tap. `start()` MUST run inside a user gesture — mobile
 * browsers reject getUserMedia otherwise and iOS will not even prompt. Voice
 * processing is turned OFF so the pitch detector sees the true waveform.
 */
export class MicTap {
  private ctx: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private analyser: AnalyserNode | null = null;
  private frame = new Float32Array(FFT_SIZE);

  get sampleRate(): number {
    return this.ctx?.sampleRate ?? 48000;
  }

  async start(): Promise<void> {
    if (!window.isSecureContext) {
      throw new MicrophoneError(
        'insecure',
        'The microphone needs a secure connection (https). Open the site over https and try again.',
      );
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new MicrophoneError('missing', 'This browser has no microphone API. You can still tune by ear with the reference tones, or analyse a recording.');
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
        video: false,
      });
    } catch (err) {
      const name = (err as DOMException)?.name ?? '';
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        throw new MicrophoneError(
          'denied',
          'Microphone access was blocked. You can still tune by ear with the reference tones, or analyse a recording.',
        );
      }
      if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        throw new MicrophoneError('missing', 'No microphone was found. Tune by ear with the reference tones, or analyse a recording.');
      }
      throw new MicrophoneError('unknown', `The microphone could not be opened (${name || 'unknown error'}).`);
    }

    const ctx = makeContext();
    if (ctx.state === 'suspended') {
      try {
        await ctx.resume();
      } catch {
        /* best effort */
      }
    }
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = FFT_SIZE;
    analyser.smoothingTimeConstant = 0;
    source.connect(analyser);
    // Deliberately NOT connected to ctx.destination.

    this.ctx = ctx;
    this.stream = stream;
    this.source = source;
    this.analyser = analyser;
    this.frame = new Float32Array(analyser.fftSize);

    window.addEventListener('pagehide', this.stop);
  }

  /** Latest time-domain frame from the mic (values ~[-1, 1]). */
  readFrame(): Float32Array | null {
    if (!this.analyser) return null;
    this.analyser.getFloatTimeDomainData(this.frame as Float32Array<ArrayBuffer>);
    return this.frame;
  }

  stop = (): void => {
    window.removeEventListener('pagehide', this.stop);
    if (this.stream) {
      for (const t of this.stream.getTracks()) {
        try {
          t.stop();
        } catch {
          /* already stopped */
        }
      }
    }
    try {
      this.source?.disconnect();
      this.analyser?.disconnect();
    } catch {
      /* noop */
    }
    if (this.ctx && this.ctx.state !== 'closed') this.ctx.close().catch(() => {});
    this.stream = null;
    this.source = null;
    this.analyser = null;
    this.ctx = null;
  };

  get active(): boolean {
    return this.stream !== null;
  }

  /** True once every track has ended — the teardown test hook. */
  fullyStopped(): boolean {
    if (!this.stream) return true;
    return this.stream.getTracks().every((t) => t.readyState === 'ended');
  }
}

export interface DecodedAudio {
  samples: Float32Array;
  sampleRate: number;
  duration: number;
}

/** Average all channels to mono. */
export function downmix(buffer: AudioBuffer): Float32Array {
  const channels = buffer.numberOfChannels;
  const out = new Float32Array(buffer.length);
  if (channels === 0) return out;
  for (let c = 0; c < channels; c++) {
    const data = buffer.getChannelData(c);
    for (let i = 0; i < out.length; i++) out[i] += data[i];
  }
  if (channels > 1) for (let i = 0; i < out.length; i++) out[i] /= channels;
  return out;
}

/** Decode an uploaded audio file to mono PCM. */
export async function decodeAudioFile(file: File): Promise<DecodedAudio> {
  const bytes = await file.arrayBuffer();
  if (bytes.byteLength === 0) throw new Error('That file is empty.');
  const ctx = new OfflineAudioContext(1, 1, 48000);
  let buffer: AudioBuffer;
  try {
    buffer = await ctx.decodeAudioData(bytes);
  } catch {
    throw new Error(`Your browser could not decode “${file.name}”. Try an MP3, M4A, WAV, OGG or FLAC file.`);
  }
  return { samples: downmix(buffer), sampleRate: buffer.sampleRate, duration: buffer.duration };
}

/** Render a Float32 mono buffer to a playable WAV Blob is done in wav.ts. */
